import React, { useState } from 'react';
import axios from 'axios';
import { ScrollText, Plus, Pencil, Trash2, Save, Lock, Unlock, ShieldAlert, AlertCircle } from 'lucide-react';
import { Modal, SearchBar, matchesSearch, formatDate, confirmDialog } from './shared';

export default function ChangelogTab({
  user, authHeaders, API_URL,
  changelog, fetchChangelog, categories, searchTerm, setSearchTerm
}) {
  const [showCreateChangelog, setShowCreateChangelog] = useState(false);
  const [editingChangelog, setEditingChangelog] = useState(null);
  const [changelogForm, setChangelogForm] = useState({ title: '', content: '', category: 'Allgemein', confidential: false });
  const [changelogCategory, setChangelogCategory] = useState('Alle');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const categoryNames = ['Alle', ...categories.map(c => c.name)];

  const handleCreateChangelog = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    try {
      await axios.post(`${API_URL}/api/community/changelog`, changelogForm, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setChangelogForm({ title: '', content: '', category: 'Allgemein', confidential: false });
      setShowCreateChangelog(false);
      fetchChangelog();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleUpdateChangelog = async (e) => {
    e.preventDefault();
    if (!editingChangelog) return;
    setFormLoading(true); setFormError('');
    try {
      await axios.put(`${API_URL}/api/community/changelog/${editingChangelog.id}`, changelogForm, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setEditingChangelog(null); setShowCreateChangelog(false);
      fetchChangelog();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleDeleteChangelog = async (entryId) => {
    if (!(await confirmDialog('Eintrag wirklich löschen?', { title: 'Eintrag löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/changelog/${entryId}`, { headers: authHeaders });
      fetchChangelog();
    } catch (e) { console.error(e); }
  };

  const filtered = changelog.filter(c =>
    matchesSearch(c, searchTerm, ['title', 'content', 'author_name', 'category']) &&
    (changelogCategory === 'Alle' || c.category === changelogCategory)
  );

  return (
    <div className="tab-content" data-testid="changelog-content">
      <div className="section-header">
        <h2><ScrollText size={20} /> Change-Log</h2>
        <button onClick={() => { setChangelogForm({ title: '', content: '', category: 'Allgemein', confidential: false }); setEditingChangelog(null); setShowCreateChangelog(true); setFormError(''); }} className="btn-sm" data-testid="create-changelog-btn"><Plus size={16} /> Neuer Eintrag</button>
      </div>
      <div className="news-categories">
        {categoryNames.map(cat => (
          <button key={cat} className={`cat-btn ${changelogCategory === cat ? 'active' : ''}`} onClick={() => setChangelogCategory(cat)}>{cat}</button>
        ))}
      </div>
      <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Change-Log durchsuchen..." />
      {filtered.length === 0 ? (
        <div className="empty-state"><ScrollText size={32} /><p>{searchTerm || changelogCategory !== 'Alle' ? 'Keine Treffer.' : 'Noch keine Eintraege.'}</p></div>
      ) : (
        filtered.map(entry => (
          <div key={entry.id} className={`changelog-card ${entry.confidential ? 'confidential' : ''}`} data-testid={`changelog-${entry.id}`}>
            <div className="changelog-header">
              <div className="changelog-title-row">
                {entry.confidential ? <Lock size={14} className="confidential-icon" /> : <Unlock size={14} className="public-icon" />}
                <h3>{entry.title}</h3>
                {entry.category && <span className="news-cat-badge">{entry.category}</span>}
                <span className={`changelog-badge ${entry.confidential ? 'badge-confidential' : 'badge-public'}`}>{entry.confidential ? 'Vertraulich' : 'Teilbar'}</span>
              </div>
              <span className="changelog-meta">{entry.author_name} &middot; {formatDate(entry.created_at)}</span>
            </div>
            <p className="changelog-content">{entry.content}</p>
            <div className="changelog-actions">
              <button className="btn-icon" onClick={() => { setEditingChangelog(entry); setChangelogForm({ title: entry.title, content: entry.content, category: entry.category || 'Allgemein', confidential: entry.confidential }); setShowCreateChangelog(true); setFormError(''); }}><Pencil size={13} /> Bearbeiten</button>
              <button className="btn-icon danger" onClick={() => handleDeleteChangelog(entry.id)}><Trash2 size={13} /> Löschen</button>
            </div>
          </div>
        ))
      )}

      {/* Changelog Modal */}
      <Modal show={showCreateChangelog} onClose={() => { setShowCreateChangelog(false); setEditingChangelog(null); }} title={editingChangelog ? 'Eintrag bearbeiten' : 'Neuer Change-Log Eintrag'} preventBackdropClose>
        <form onSubmit={editingChangelog ? handleUpdateChangelog : handleCreateChangelog} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={changelogForm.title} onChange={e => setChangelogForm({...changelogForm, title: e.target.value})} required placeholder="Kurze Zusammenfassung" data-testid="changelog-title" /></div>
          <div className="form-group"><label>Beschreibung</label><textarea value={changelogForm.content} onChange={e => setChangelogForm({...changelogForm, content: e.target.value})} rows={5} required placeholder="Details..." data-testid="changelog-content-input" /></div>
          <div className="form-row">
            <div className="form-group">
              <label>Kategorie</label>
              <select value={changelogForm.category || 'Allgemein'} onChange={e => setChangelogForm({...changelogForm, category: e.target.value})} className="modal-select">
                {categoryNames.filter(c => c !== 'Alle').map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="checkbox-label confidential-toggle">
                <input type="checkbox" checked={changelogForm.confidential} onChange={e => setChangelogForm({...changelogForm, confidential: e.target.checked})} />
                <ShieldAlert size={16} />
                <span>Vertraulich</span>
              </label>
              <span className="checkbox-hint">{changelogForm.confidential ? 'Nur Admin/Coach' : 'Teilbar'}</span>
            </div>
          </div>
          <button type="submit" className="btn-login" disabled={formLoading}>
            {editingChangelog ? <><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</> : <><Plus size={16} /> {formLoading ? 'Erstellen...' : 'Eintrag erstellen'}</>}
          </button>
        </form>
      </Modal>
    </div>
  );
}
